import React from 'react';
import { PageContentBlock } from '@/types/page';
import { cn } from '@/lib/utils';

interface RenderContentProps {
  content: {
    blocks: PageContentBlock[];
    time?: number;
    version?: string;
  } | null | undefined;

  // Additional class names to apply to the container
  className?: string;
}

const renderBlock = (block: PageContentBlock, index: number): React.ReactNode => {
  // Ensure block has a valid type
  if (!block.type) {
    console.warn('Block is missing type:', block);
    return null;
  }

  const key = block.id || `block-${index}`;
  
  try {
    switch (block.type) {
      case 'header': {
        const level = Math.min(Math.max(block.data.level || 2, 1), 6);
        const HeadingTag = `h${level}` as keyof JSX.IntrinsicElements;
        return (
          <HeadingTag key={key} className="text-heritage mt-6 mb-4 font-bold">
            {block.data.text || ''}
          </HeadingTag>
        );
      }
      
      case 'paragraph':
        return (
          <p key={key} className="mb-4 leading-relaxed">
            {block.data.text || ''}
          </p>
        );
      
      case 'list': {
        const ListTag = block.data.style === 'ordered' ? 'ol' : 'ul';
        const items = Array.isArray(block.data.items) ? block.data.items : [];
        
        return (
          <ListTag 
            key={key} 
            className={cn('mb-4 pl-6', {
              'list-decimal': block.data.style === 'ordered',
              'list-disc': block.data.style !== 'ordered'
            })}
          >
            {items.map((item, i) => (
              <li key={i} className="mb-1">
                {item}
              </li>
            ))}
          </ListTag>
        );
      }
      
      case 'image': {
        const imageUrl = block.data.file?.url;
        if (!imageUrl) {
          console.warn('Image block is missing URL:', block);
          return null;
        }
        
        return (
          <figure key={key} className="my-6">
            <div className="overflow-hidden rounded-lg bg-gray-100">
              <img 
                src={imageUrl}
                alt={block.data.caption || ''} 
                className="w-full h-auto max-h-[600px] object-contain"
                loading="lazy"
              />
            </div>
            {block.data.caption && (
              <figcaption className="mt-2 text-sm text-center text-muted-foreground">
                {block.data.caption}
              </figcaption>
            )}
          </figure>
        );
      }
      
      case 'quote':
        return (
          <blockquote 
            key={key}
            className="border-l-4 border-heritage pl-4 py-2 my-6 italic text-gray-700"
          >
            <p className="text-lg">{block.data.text || ''}</p>
            {block.data.caption && (
              <footer className="mt-2 text-base font-medium not-italic text-gray-600">
                — {block.data.caption}
              </footer>
            )}
          </blockquote>
        );
      
      case 'delimiter':
        return (
          <div key={key} className="my-8 flex justify-center">
            <hr className="w-1/4 border-t-2 border-gray-200" />
          </div>
        );
      
      case 'html': {
        // Handle HTML blocks - render the HTML content directly
        const htmlContent = block.data?.html || block.data?.text || '';
        if (!htmlContent) {
          return null;
        }
        return (
          <div 
            key={key} 
            className="my-4 prose prose-lg max-w-none"
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
        );
      }
      
      default:
        console.warn(`Unsupported block type: ${block.type}`, block);
        return null;
    }
  } catch (error) {
    console.error('Error rendering block:', error, block);
    return null;
  }
};

export const RenderContent: React.FC<RenderContentProps> = ({ 
  content, 
  className 
}) => {
  // Handle null/undefined content
  if (!content) {
    return (
      <div className={cn('text-muted-foreground italic', className)}>
        No content available
      </div>
    );
  }

  // Handle empty blocks array
  if (!Array.isArray(content.blocks) || content.blocks.length === 0) {
    return (
      <div className={cn('text-muted-foreground italic', className)}>
        No content available
      </div>
    );
  }

  // Filter out null/undefined blocks and render valid ones
  const validBlocks = content.blocks.filter(Boolean);
  
  if (validBlocks.length === 0) {
    return (
      <div className={cn('text-muted-foreground italic', className)}>
        No valid content to display
      </div>
    );
  }

  return (
    <div className={cn('prose prose-lg max-w-none', className)}>
      {validBlocks.map((block, index) => 
        block ? renderBlock(block, index) : null
      )}
    </div>
  );
};

// Add display name for better debugging
RenderContent.displayName = 'RenderContent';
